//FormItemEditor.tsx
import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  FormControl,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ErrorIcon from "@mui/icons-material/Error";
import { FormData, CustomFormSelectItem } from "../../../data/WorkFlowEditorType";
import theme from "../../../../../style/theme";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

// タイプマーピング
const typeLabelMap: Record<string, string> = {
  text: "テキスト",
  textLong: "テキストボックス",
  // autocomplete: "オートコンプリート", // 未実装のため一時的に非表示
  select: "選択リスト",
  date: "日付",
  switch: "スイッチ",
  fileUpload: "ファイルアップロード",
  linkList: "リンクリスト",
  applicationNumber: "申請番号",
  amount: "金額",
  richText:"リッチテキスト"
};

const typeOptions = Object.keys(typeLabelMap);

{/* ドラッグできるセレクトアイテムカード*/ }
const DraggableSelectItemCard: React.FC<{
  item: CustomFormSelectItem;
  index: number;
  onChange: (updated: CustomFormSelectItem) => void;
  onDelete: () => void;
  error?: string;
  onClearError?: () => void;
}> = ({ item, index, onChange, onDelete, error, onClearError }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 6,
    padding: 6,
    border: "1px solid #ccc",
    borderRadius: "6px",
    backgroundColor: "#f5f5f5",
    opacity: isDragging ? 0.9 : 1,
    display: "flex",
    alignItems: "center",
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <Box {...attributes} {...listeners} sx={{
        cursor: "grab",
        mr: 1,
        px: 1,
        py: 0.5,
        borderRadius: "7px",
        border: "1px solid transparent",
        backgroundColor: "#f0f0f0",
        display: "flex",
        alignItems: "center",
        "&:hover": {
          border: "1px solid d0d0d0",
          backgroundColor: "#d0d0d0",
        },
      }}>
        <DragIndicatorIcon fontSize="small" sx={{ color: "#888" }} />
      </Box>

      <TextField
        label={`選択肢 ${index + 1}`}
        value={item.label}
        onChange={(e) => {
          onChange({ ...item, label: e.target.value, value: e.target.value }); // 入力値でlabelとvalueを両方更新
          if (error && e.target.value.trim() && onClearError) {
            onClearError();
          }
        }}
        fullWidth
        sx={{
          m: 1,
          '& .MuiFormHelperText-root': {
            fontSize: '1rem'
          }
        }}
        error={!!error}
        helperText={error}
      />
      <IconButton onClick={onDelete}>
        <DeleteIcon color="error" />
      </IconButton>
    </Box>
  );
};

//セレクトアイテム編集コンポーネント
const SelectItemEditor: React.FC<{
  selectItemList: CustomFormSelectItem[];
  onChange: (updated: CustomFormSelectItem[]) => void;
  validationErrors?: { [key: string]: string };
  clearFieldError?: (fieldPath: string) => void;
  formItemId?: string;
}> = ({ selectItemList, onChange, validationErrors, clearFieldError, formItemId }) => {
  const sensors = useSensors(useSensor(PointerSensor));
  //順番調整
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = selectItemList.findIndex((item) => item.id === active.id);
      const newIndex = selectItemList.findIndex((item) => item.id === over?.id);
      const reordered = arrayMove(selectItemList, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        selectOrder: idx + 1,
      }));
      onChange(reordered);
    }
  };
  //選択肢追加
  const handleAdd = () => {
    const newItem: CustomFormSelectItem = {
      id: crypto.randomUUID(),
      selectOrder: selectItemList.length + 1,
      value: "",
      label: "",
    };
    onChange([...selectItemList, newItem]);
  };

  //選択肢削除
  const handleDelete = (id: string) => {
    const updated = selectItemList
      .filter((item) => item.id !== id)
      .map((item, idx) => ({
        ...item,
        selectOrder: idx + 1,
      }));
    onChange(updated);
  };
  return (
    <Box sx={{ mt: 2, p: 1, border: "1px dashed #ccc", borderRadius: 2 }}>
      <Typography variant="subtitle2">選択肢編集</Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={selectItemList.map((item) => item.id)} strategy={verticalListSortingStrategy}>

          {selectItemList.map((item, index) => (
            <DraggableSelectItemCard
              key={item.id}
              item={item}
              index={index}
              onChange={(updated) => {
                const newList = [...selectItemList];
                newList[index] = updated;
                onChange(newList);
              }}
              onDelete={() => handleDelete(item.id)}
              error={validationErrors?.[item.id]}
              onClearError={() => {
                if (clearFieldError && formItemId) {
                  clearFieldError(`formItems.${formItemId}.selectItems.${item.id}`);
                }
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Box mt={1} display="flex" justifyContent="center">
        <Tooltip title="選択肢を追加">
          <IconButton onClick={handleAdd}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};


{/* 展開できるフォームアイテムカード*/ }
const ExpandableFormItemCard: React.FC<{
  item: FormData;
  index: number;
  onChange: (updated: FormData) => void;
  onDelete: () => void;
  validationErrors?: { label?: string; type?: string; selectItems?: { [key: string]: string } };
  clearFieldError?: (fieldPath: string) => void;
}> = ({ item, index, onChange, onDelete, validationErrors, clearFieldError }) => {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.formId });

  //入力値のエラーチェック
  const hasErrors = !!(validationErrors?.label || validationErrors?.type || validationErrors?.selectItems);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 6,
    padding: 6,
    border: hasErrors ? "2px solid #d32f2f" : "1px solid #ccc",
    borderRadius: "6px",
    backgroundColor: "#f9f9f9",
    opacity: isDragging ? 0.9 : 1,
  };

  // カードの展開・折りたたみを切り替える
  const toggleExpand = () => setExpanded((prev) => !prev);

  const AMOUNT_DEFAULT_VALUE_REGEX = /^(?:$|0|[1-9][0-9]*)$/;

  const isValidAmountDefaultValue = (v: string) => {
    if (v !== "" && v.trim() === "") return false;
    return AMOUNT_DEFAULT_VALUE_REGEX.test(v);
  };

  const amountDefaultValueErrorMessage =
    "金額のデフォルト値は 0以上の整数で入力してください";


  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: "relative"
      }}
    >
      <Box display="flex" alignItems="center">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            mr: 1,
            px: 1,
            py: 0.5,
            borderRadius: "7px",
            border: "1px solid transparent",
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              border: "1px solid d0d0d0",
              backgroundColor: "#d0d0d0",
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ color: "#888" }} />
        </Box>

        {/* 番号とテキスト部分 */}
        <Box display="flex" alignItems="center" flexGrow={1} ml={1}>
          {/* 丸い番号 */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              mr: 1,
            }}
          >
            {item.itemOrder}
          </Box>

          {/* テキスト */}
          <Typography variant="body1">
            {item.label || "（ラベル未設定）"}
          </Typography>

          {/* エラーアイコン */}
          {hasErrors && (
            <ErrorIcon sx={{ color: "#d32f2f", ml: 1 }} />
          )}
        </Box>

        <IconButton onClick={toggleExpand} sx={{}}>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>

        <IconButton onClick={onDelete}>
          <DeleteIcon color="error" />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box mt={2}>
          <TextField
            label="ラベル名"
            value={item.label}
            onChange={(e) => {
              onChange({ ...item, label: e.target.value });
              if (validationErrors?.label && e.target.value.trim() && clearFieldError) {
                clearFieldError(`formItems.${item.formId}.label`);
              }
            }}
            fullWidth
            sx={{
              mb: 1,
              '& .MuiFormHelperText-root': {
                fontSize: '1rem'
              }
            }}
            error={!!validationErrors?.label}
            helperText={validationErrors?.label}
          />
          <FormControl fullWidth error={!!validationErrors?.type} sx={{ mb: 1 }}>
            <Select
              value={item.type ?? ""}
              onChange={(e) => {
                onChange({ ...item, type: e.target.value });
                if (validationErrors?.type && e.target.value && clearFieldError) {
                  clearFieldError(`formItems.${item.formId}.type`);
                }
              }}
              displayEmpty
            >
              <MenuItem value="">タイプを選択</MenuItem>
              {typeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {typeLabelMap[type] ?? type}
                </MenuItem>
              ))}
            </Select>
            {validationErrors?.type && (
              <FormHelperText sx={{ fontSize: '1rem' }}>
                {validationErrors.type}
              </FormHelperText>
            )}
          </FormControl>
          {item.type !== "linkList" && item.type !== "fileUpload" && item.type !== "switch" && (
            <TextField
              label="デフォルト値"
              value={item.defaultValue ?? ""}
              // onChange={(e) => onChange({ ...item, defaultValue: e.target.value })}
              onChange={(e) => {
                const next = e.target.value;
                onChange({ ...item, defaultValue: next });
                if (
                  item.type === "amount" &&
                  clearFieldError &&
                  isValidAmountDefaultValue(next)
                ) {
                  clearFieldError(`formItems.${item.formId}.defaultValue`);
                }
              }}
              fullWidth
              sx={{ mt: 1 }}
              // amount のときだけ制約 & エラー表示
              inputProps={
                item.type === "amount"
                  ? { inputMode: "numeric", pattern: "[0-9]*" }
                  : undefined
              }
              error={
                item.type === "amount"
                  ? !isValidAmountDefaultValue(String(item.defaultValue ?? ""))
                  : false
              }
              helperText={
                item.type === "amount" &&
                  !isValidAmountDefaultValue(String(item.defaultValue ?? ""))
                  ? amountDefaultValueErrorMessage
                  : undefined
              }
            />)}
          <FormControlLabel
            control={
              <Checkbox
                checked={item.isRequired ?? false}
                onChange={(e) =>
                  onChange({ ...item, isRequired: e.target.checked })
                }
              />
            }
            label="必須項目"
          />

          {item.type === "select" && (
            <SelectItemEditor
              selectItemList={item.selectItemList ?? []}
              onChange={(updatedList) => {
                onChange({ ...item, selectItemList: updatedList });
              }}
              validationErrors={validationErrors?.selectItems}
              clearFieldError={clearFieldError}
              formItemId={item.formId}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

{/* フォームアイテムエディターコンポーネント */ }
const FormItemEditor: React.FC<{
  formItems: FormData[];
  setFormItems: (items: FormData[]) => void;
  validationErrors?: { [key: string]: { label?: string; type?: string; selectItems?: { [key: string]: string } } };
  clearFieldError?: (fieldPath: string) => void;
}> = ({ formItems, setFormItems, validationErrors, clearFieldError }) => {
  const sensors = useSensors(useSensor(PointerSensor));

  // ドラッグ&ドロップ完了時にフォームアイテムの順番を更新する
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = formItems.findIndex((item) => item.formId === active.id);
      const newIndex = formItems.findIndex((item) => item.formId === over?.id);
      const reordered = arrayMove(formItems, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        itemOrder: idx + 1,
      }));
      setFormItems(reordered);
    }
  };

  // フォームアイテムを削除し、残りのアイテムの表示順序を振り直す
  const handleDeleteFormItem = (formId: string) => {
    const updated = formItems
      .filter((item) => item.formId !== formId)
      .map((item, idx) => ({
        ...item,
        itemOrder: idx + 1,
      }));
    setFormItems(updated);
  };

  // 新規フォームアイテムを追加し、フォームアイテム空エラーをクリアする
  const handleAddFormItem = () => {
    const newItem: FormData = {
      formId: crypto.randomUUID(),
      label: "",
      type: "",
      isRequired: false,
      defaultValue: "",
      itemOrder: formItems.length + 1,
      selectItemList: [],
    };
    setFormItems([...formItems, newItem]);
    // 空エラーをクリア
    if (clearFieldError) {
      clearFieldError('formItemsEmpty');
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ mx: 1, display: "flex", alignItems: "center" }}
      >
        左側の <DragIndicatorIcon fontSize="small" sx={{ color: "#888" }} />
        アイコンをドラッグして項目の順番を変更できます
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={formItems.map((item) => item.formId)}
          strategy={verticalListSortingStrategy}
        >
          {formItems
            .sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0))
            .map((item, index) => (
              <ExpandableFormItemCard
                key={item.formId}
                item={item}
                index={index}
                onChange={(updated) => {
                  const newItems = [...formItems];
                  newItems[index] = updated;
                  setFormItems(newItems);
                }}
                onDelete={() => handleDeleteFormItem(item.formId)}
                validationErrors={validationErrors?.[item.formId]}
                clearFieldError={clearFieldError}
              />
            ))}
        </SortableContext>
      </DndContext>

      <Box mt={2} display="flex" justifyContent="center">
        <Tooltip title="追加">
          <IconButton onClick={handleAddFormItem}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default FormItemEditor;